"use client"

import { forwardRef } from "react"
import type { ElementType } from "react"
import { useSidebarContext } from "./hooks/useSidebarContext"
import type { SidebarToggleProps, SidebarToggleRenderProp, SidebarCollapseMode } from "./Sidebar.types"
import { SidebarIconLibrary } from "./Sidebar.types"
import {
  SIDEBAR_TAILWIND_CLASSES,
  SIDEBAR_ARIA_LABELS,
  SIDEBAR_DATA_ATTRIBUTES,
} from "./Sidebar.constants"

/**
 * Icono de chevron SVG inline (default toggle icon)
 */
function DefaultToggleIcon({ collapseMode }: { collapseMode: SidebarCollapseMode }) {
  return (
    <svg className="toggle-icon w-3 h-3 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {collapseMode === "hide" ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      )}
    </svg>
  )
}

/**
 * Componente de botón toggle del Sidebar
 *
 * @description
 * Botón que controla el estado abierto/cerrado del sidebar.
 * Renderiza un `<label>` por defecto que controla un checkbox oculto,
 * permitiendo gestionar el estado con CSS puro (:checked selector).
 *
 * Características:
 * - Soporta render props para acceso al estado (open, active)
 * - Renderiza icono de chevron por defecto (customizable)
 * - Aplica data attributes para styling CSS
 * - Accesibilidad completa (aria-label, aria-expanded)
 * - Soporta prop `as` para usar elemento/componente custom
 *
 * @example
 * ```tsx
 * // Uso básico con icono por defecto
 * <SidebarToggle />
 * ```
 *
 * @example
 * ```tsx
 * // Con render props para estado
 * <SidebarToggle>
 *   {({ open, active }) => (
 *     <div className={cn(active && 'bg-blue-500')}>
 *       {open ? <CloseIcon /> : <MenuIcon />}
 *     </div>
 *   )}
 * </SidebarToggle>
 * ```
 *
 * @example
 * ```tsx
 * // Con icono custom
 * <SidebarToggle className="bg-zinc-800">
 *   <CustomChevronIcon />
 * </SidebarToggle>
 * ```
 *
 * @example
 * ```tsx
 * // Como botón real (sin checkbox)
 * <SidebarToggle as="button" onClick={() => setOpen(!open)}>
 *   Toggle
 * </SidebarToggle>
 * ```
 */
export const SidebarToggle = forwardRef<HTMLLabelElement | HTMLButtonElement, SidebarToggleProps>(
  function SidebarToggle(
    { children, iconLibrary, className = "", style, "aria-label": ariaLabel, as, ...props },
    ref,
  ) {
    const { open, setOpen, toggleId, collapseMode } = useSidebarContext()

    // Determinar si children es render prop
    const isRenderProp = typeof children === "function"

    // Estado del toggle
    const toggleState = {
      active: open,
      open,
    }

    // Elemento a renderizar (default: label)
    const Component = (as || "label") as ElementType

    // Wrapper con estilos base
    const toggleClassName = `${SIDEBAR_TAILWIND_CLASSES.toggleButton} ${SIDEBAR_TAILWIND_CLASSES.toggleButtonWrapper} bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 ${className}`

    // Props comunes
    const commonProps = {
      className: toggleClassName,
      style,
      "aria-label": ariaLabel || SIDEBAR_ARIA_LABELS.toggleSidebar,
      ...{ [SIDEBAR_DATA_ATTRIBUTES.ACTIVE]: open },
      ...props,
    }

    // Si es label (default), controla el checkbox
    if (!as || as === "label") {
      return (
        <>
          {/* Checkbox oculto para gestionar estado con CSS */}
          <input
            type="checkbox"
            id={toggleId}
            className={SIDEBAR_TAILWIND_CLASSES.srOnly}
            checked={open}
            onChange={(e) => setOpen(e.target.checked)}
            aria-label={ariaLabel || SIDEBAR_ARIA_LABELS.toggleSidebar}
          />

          <label ref={ref as React.Ref<HTMLLabelElement>} htmlFor={toggleId} {...commonProps}>
            {isRenderProp ? (
              (children as SidebarToggleRenderProp)(toggleState)
            ) : children ? (
              children
            ) : (
              <DefaultToggleIcon collapseMode={collapseMode} />
            )}
          </label>
        </>
      )
    }

    // Si es otro elemento (button, div, etc.)
    return (
      <Component
        ref={ref}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        role="button"
        {...commonProps}
      >
        {isRenderProp ? (
          (children as SidebarToggleRenderProp)(toggleState)
        ) : children ? (
          children
        ) : (
          <DefaultToggleIcon collapseMode={collapseMode} />
        )}
      </Component>
    )
  },
)
